import './RequiredFieldsLegend.css';

export const DEFAULT_REQUIRED_FIELDS_TEXT = 'fields';

export interface RequiredFieldsLegendProps {
  /**
   * Trailing noun in the legend. Combined as:
   * "All ( * ) star indicators are required {requiredFieldsText}."
   *  e.g uploads, steps, documents, etc.
   * @default 'fields'
   * @example
   * <RequiredFieldsLegend />
   * <RequiredFieldsLegend requiredFieldsText="documents" />
   */
  requiredFieldsText?: string;
  /**
   * Optional additional CSS class name.
   */
  className?: string;
  /**
   * Test ID for automated testing.
   * @default 'required-fields-legend'
   */
  'data-testid'?: string;
}

/**
 * Common component for displaying a legend that
 * explains asterisks mark required fields.  Necessary for
 * 508 accessibility compliance. For use before the
 * required fields asterisks are displayed (i.e. in
 * the parent element).
 *
 * Example displays:
 * All ( * ) star indicators are required fields.
 * All ( * ) star indicators are required steps.
 * All ( * ) star indicators are required documents.
 *
 * @example
 * <RequiredFieldsLegend />
 * <RequiredFieldsLegend requiredFieldsText="steps" />
 */
export function RequiredFieldsLegend({
  requiredFieldsText = DEFAULT_REQUIRED_FIELDS_TEXT,
  className = '',
  'data-testid': dataTestId,
}: RequiredFieldsLegendProps) {
  const baseTestId = dataTestId ?? 'required-fields-legend';

  return (
    <p
      className={`required-fields-legend ${className}`.trim()}
      data-testid={baseTestId}>
      All (
      <span
        aria-label='required'
        className='required-fields-legend__asterisk'>
        {' '}
        *{' '}
      </span>
      ) star indicators are required {requiredFieldsText}.
    </p>
  );
}

export default RequiredFieldsLegend;
